import {
  DetailType,
  ShotOnTargetAttribute,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'

import {
  AttributeConfig,
  DetailConfiguration,
  getAttributeId,
  OnSelectOptions,
} from '../../../../composables/useSportOptions'
import { hasIncidentSomeAttribute } from '../../../../Modals/utils'
import {
  onSelectTechniqueOfShot,
  onSelectTechniqueOfShotDetails,
} from '../../utils'
import { techniqueOfShotDetailsAttributes } from './techniqueOfShotDetailsAttributes'

export enum GroupName {
  Distance = 'Distance',
  Placement = 'Placement',
  TechniqueOfShot = 'Technique of shot',
  TechniqueOfShotDetails = 'Technique of shot - details',
  PressureOfDefenders = 'Pressure of defenders',
  NumberOfDefendersOnTheLineOfShot = 'Number of defenders on the line of shot',
  AdditionalAttributes = 'Additional attributes',
  Goalkeeper = 'Goalkeeper',
  LevelOfDifficultyOfGoalkeeperSaves = 'Level of difficulty of goalkeeper saves',
}

export const groups: Map<
  GroupName,
  DetailConfiguration<ShotOnTargetAttribute>
> = new Map([
  [
    GroupName.Distance,
    {
      type: DetailType.Button,
      name: GroupName.Distance,
      important: true,
      attributes: [
        ShotOnTargetAttribute.InsidePenaltyArea,
        ShotOnTargetAttribute.OutsidePenaltyArea,
      ],
      cols: 2,
      multiple: false,
    },
  ],
  [
    GroupName.Placement,
    {
      type: DetailType.Button,
      name: GroupName.Placement,
      important: true,
      attributes: [
        ShotOnTargetAttribute.UpperLeft,
        ShotOnTargetAttribute.UpperMiddle,
        ShotOnTargetAttribute.UpperRight,
        ShotOnTargetAttribute.BottomLeft,
        ShotOnTargetAttribute.BottomMiddle,
        ShotOnTargetAttribute.BottomRight,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.TechniqueOfShot,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShot,
      important: true,
      attributes: [
        ShotOnTargetAttribute.Normal,
        {
          attributeId: ShotOnTargetAttribute.FirstTouch,
          isActive: (attributes: ShotOnTargetAttribute[]) => {
            const validAttributes = [
              ShotOnTargetAttribute.Normal,
              ShotOnTargetAttribute.Volley,
            ]
            return attributes.some((attribute) =>
              validAttributes.includes(attribute),
            )
          },
        } as AttributeConfig<ShotOnTargetAttribute>,
        ShotOnTargetAttribute.Volley,
        ShotOnTargetAttribute.Head,
        ShotOnTargetAttribute.DirectFreeKick,
        ShotOnTargetAttribute.BicycleKick,
        ShotOnTargetAttribute.OtherBodyPart,
      ],
      cols: 3,
      multiple: false,
      onSelect: (opts: OnSelectOptions<ShotOnTargetAttribute>) =>
        onSelectTechniqueOfShot(opts, ShotOnTargetAttribute.FirstTouch),
    },
  ],
  [
    GroupName.TechniqueOfShotDetails,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShotDetails,
      important: true,
      attributes: techniqueOfShotDetailsAttributes,
      cols: 2,
      multiple: false,
      fulfilledWhen: (
        _attributes: ShotOnTargetAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        const isFulfilled = probableIncident.attribute_ids.some(
          (attribute) =>
            attribute === ShotOnTargetAttribute.Head ||
            attribute === ShotOnTargetAttribute.OtherBodyPart,
        )

        if (isFulfilled) return true

        return hasIncidentSomeAttribute(
          probableIncident,
          techniqueOfShotDetailsAttributes.map((attribute) =>
            getAttributeId(attribute),
          ),
        )
      },
      onSelect: (opts: OnSelectOptions<ShotOnTargetAttribute>) =>
        onSelectTechniqueOfShotDetails(opts, [
          [ShotOnTargetAttribute.RightFoot, ShotOnTargetAttribute.LeftFoot],
          [ShotOnTargetAttribute.LeftFoot, ShotOnTargetAttribute.RightFoot],
          [ShotOnTargetAttribute.Chip, ShotOnTargetAttribute.PowerShot],
          [ShotOnTargetAttribute.PowerShot, ShotOnTargetAttribute.Chip],
        ]),
    },
  ],
  [
    GroupName.PressureOfDefenders,
    {
      type: DetailType.Button,
      name: GroupName.PressureOfDefenders,
      important: true,
      attributes: [
        ShotOnTargetAttribute.NoPressure,
        ShotOnTargetAttribute.LightPressure,
        ShotOnTargetAttribute.HighPressure,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.NumberOfDefendersOnTheLineOfShot,
    {
      type: DetailType.Button,
      name: GroupName.NumberOfDefendersOnTheLineOfShot,
      important: true,
      attributes: [
        ShotOnTargetAttribute.ClearPosition,
        ShotOnTargetAttribute.OneDefender,
        ShotOnTargetAttribute.TwoOrMoreDefenders,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.AdditionalAttributes,
    {
      type: DetailType.Button,
      name: GroupName.AdditionalAttributes,
      important: false,
      attributes: [
        ShotOnTargetAttribute.BigChanceMissed,
        ShotOnTargetAttribute.OneOnOne,
        ShotOnTargetAttribute.UncomfortablePosition,
        ShotOnTargetAttribute.GoallineClearance,
        ShotOnTargetAttribute.EmptyNet,
      ],
      cols: 4,
      multiple: true,
    },
  ],
  [
    GroupName.Goalkeeper,
    {
      type: DetailType.Select,
      name: GroupName.Goalkeeper,
      important: true,
      attributes: [],
      optionsFactory: () => [],
      fulfilledWhen: (
        _: ShotOnTargetAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        return !!probableIncident.additional_info?.goalkeeper_id
      },
    },
  ],
  [
    GroupName.LevelOfDifficultyOfGoalkeeperSaves,
    {
      type: DetailType.Button,
      name: GroupName.LevelOfDifficultyOfGoalkeeperSaves,
      important: true,
      attributes: [
        ShotOnTargetAttribute.EasySave,
        ShotOnTargetAttribute.ImportantSave,
        ShotOnTargetAttribute.SpectacularSave,
      ],
      cols: 3,
      multiple: false,
      fulfilledWhen: (
        _attributes: ShotOnTargetAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        const relevantAttributes = [
          ShotOnTargetAttribute.GoallineClearance,
          ShotOnTargetAttribute.EmptyNet,
          ShotOnTargetAttribute.EasySave,
          ShotOnTargetAttribute.ImportantSave,
          ShotOnTargetAttribute.SpectacularSave,
        ]
        return probableIncident.attribute_ids.some((attributeId) =>
          relevantAttributes.includes(attributeId as ShotOnTargetAttribute),
        )
      },
    },
  ],
])
